import { supabase } from '@/integrations/supabase/client';
import { offlineDb } from '@/db/offlineDb';
import { networkService } from '@/services/network.service';
import { primaryListOfflineService } from '@/services/offline/primary-list-offline.service';
import { syncValidationService } from './SyncValidationService';
import { syncQueueService } from './sync-queue-manager';
import { syncOptimizationService } from './SyncOptimizationService';
import OfflineLogger from '@/utils/offlineLogger';

export interface SyncResult {
  success: boolean;
  entity: string;
  count: number;
  error?: string;
}

export interface InitialSyncStatus {
  isComplete: boolean;
  results: SyncResult[];
  timestamp: number;
  userId: string | null;
  totalEntities: number;
  successfulEntities: number;
}

class InitialSyncService {
  private syncLock = false;
  private syncPromise: Promise<InitialSyncStatus> | null = null;

  async performInitialSync(
    userId: string | null,
    expectEmpty = false
  ): Promise<InitialSyncStatus> {
    OfflineLogger.info('InitialSyncService.performInitialSync', 'Invoked', {
      userId,
      stack: new Error().stack
    });
    // Evita sync multipli simultanei
    if (!userId) {
      OfflineLogger.warn('InitialSyncService', 'No user id provided for initial sync');
      return this.createFailedStatus(null, 'No user id');
    }
    if (this.syncLock) {
      OfflineLogger.info('InitialSyncService', 'Sync already in progress, returning existing promise');
      return this.syncPromise || this.createFailedStatus(userId, 'Sync already in progress');
    }

    this.syncLock = true;
    
    this.syncPromise = this.executeOptimizedSync(userId, expectEmpty);
    
    try {
      return await this.syncPromise;
    } finally {
      this.syncLock = false;
      this.syncPromise = null;
    }
  }

  cancelSync(): void {
    syncQueueService.cancelAll();
    this.syncLock = false;
    this.syncPromise = null;
  }

  private async executeOptimizedSync(
    userId: string | null,
    expectEmpty = false
  ): Promise<InitialSyncStatus> {
    const startTime = Date.now();
    
    const idDisplay = userId ? userId.substring(0, 8) : 'unknown';
    OfflineLogger.info('InitialSyncService', `Starting optimized initial sync for user ${idDisplay}`);

    try {
      // ✅ NUOVO: Usa il servizio di ottimizzazione per sync intelligente
      const optimizationResult = await syncOptimizationService.performOptimizedInitialSync(
        userId,
        expectEmpty
      );
      
      if (!optimizationResult.success) {
        OfflineLogger.warn(
          'InitialSyncService',
          'Optimization failed, falling back to standard sync'
        );
        return await this.executeStandardSync(userId, expectEmpty);
      }

      // ✅ Prepara dati obbligatori prima della validazione
      await this.ensurePrimaryListExists(userId);

      // ✅ NUOVO: Validazione completa post-sync
      OfflineLogger.info('InitialSyncService.executeOptimizedSync', 'Invoking performComprehensiveValidation', {
        userId,
        stack: new Error().stack
      });
      const validation = await syncValidationService.performComprehensiveValidation(
        userId,
        expectEmpty
      );
      
      // Se validazione fallisce per entità critiche, prova sync standard
      if (!validation.isValid && validation.criticalMissing.length > 0) {
        OfflineLogger.warn('InitialSyncService', 'Critical validation failed, performing full sync');
        return await this.executeStandardSync(userId, expectEmpty);
      }


      const syncStatus: InitialSyncStatus = {
        isComplete: validation.isValid,
        results: optimizationResult.entitiesSynced.map(entity => ({
          success: true,
          entity,
          count: validation.details[entity] || 0
        })),
        timestamp: Date.now(),
        userId,
        totalEntities: 6,
        successfulEntities: optimizationResult.entitiesSynced.length
      };

      const duration = Date.now() - startTime;
      OfflineLogger.info('InitialSyncService', 
        `Optimized sync completed in ${duration}ms using ${optimizationResult.strategy} strategy`
      );

      await this.storeSyncStatus(syncStatus);
      return syncStatus;

    } catch (error) {
      OfflineLogger.error('InitialSyncService.executeOptimizedSync', error);
      // Fallback al sync standard in caso di errore
      return await this.executeStandardSync(userId, expectEmpty);
    }
  }

  private async executeStandardSync(
    userId: string | null,
    expectEmpty = false
  ): Promise<InitialSyncStatus> {
    // ✅ Mantieni il sync standard esistente come fallback
    const startTime = Date.now();
    const results: SyncResult[] = [];
    
    const idDisplay = userId ? userId.substring(0, 8) : 'unknown';
    OfflineLogger.info('InitialSyncService', `Starting standard sync for user ${idDisplay}`);

    // Lista delle entità da sincronizzare in ordine di priorità
    const syncTasks = [
      { name: 'user_categories', fn: () => this.syncUserCategories(userId) },
      { name: 'user_products', fn: () => this.syncUserProducts(userId) },
      { name: 'shopping_lists', fn: () => this.syncShoppingLists(userId) },
      { name: 'shopping_items', fn: () => this.syncShoppingItems(userId) },
      { name: 'user_shops', fn: () => this.syncUserShops(userId) },
      { name: 'recurring_products', fn: () => this.syncRecurringProducts(userId) }
    ];

    // ✅ NUOVO: Usa sync queue per gestione ottimizzata
    for (const task of syncTasks) {
      try {
        await syncQueueService.addTask(task.name, userId, 'full-sync', 1, 20000);
      } catch (error) {
        OfflineLogger.error('InitialSyncService', `Failed to queue ${task.name}: ${error}`);
      }
    }

    // Attendi completamento di tutte le task critiche
    await syncQueueService.waitForCompletion(45000);

    // ✅ Prepara dati obbligatori prima della validazione
    await this.ensurePrimaryListExists(userId);

    // ✅ NUOVO: Usa validazione per verificare risultati
    OfflineLogger.info('InitialSyncService.executeStandardSync', 'Invoking performComprehensiveValidation', {
      userId,
      stack: new Error().stack
    });
    const validation = await syncValidationService.performComprehensiveValidation(
      userId,
      expectEmpty
    );
    
    // Crea risultati basati sulla validazione
    syncTasks.forEach(task => {
      const count = validation.details[task.name] || 0;
      const success = !validation.criticalMissing.includes(task.name);
      
      results.push({
        success,
        entity: task.name,
        count,
        error: success ? undefined : `Validation failed for ${task.name}`
      });
    });


    const syncStatus: InitialSyncStatus = {
      isComplete: validation.isValid,
      results,
      timestamp: Date.now(),
      userId,
      totalEntities: syncTasks.length,
      successfulEntities: results.filter(r => r.success).length
    };

    const duration = Date.now() - startTime;
    OfflineLogger.info('InitialSyncService', 
      `Standard sync completed in ${duration}ms. Success: ${syncStatus.successfulEntities}/${syncStatus.totalEntities}`
    );

    await this.storeSyncStatus(syncStatus);
    return syncStatus;
  }

  private async executeWithRetry<T>(
    fn: () => Promise<T>, 
    taskName: string, 
    maxRetries: number
  ): Promise<T> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        if (attempt > 0) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
          OfflineLogger.info('InitialSyncService', `Retrying ${taskName} in ${delay}ms (attempt ${attempt + 1})`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }

        return await fn();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error');
        OfflineLogger.warn('InitialSyncService', `${taskName} attempt ${attempt + 1} failed: ${lastError.message}`);
      }
    }

    throw lastError || new Error(`${taskName} failed after ${maxRetries + 1} attempts`);
  }

  private async syncUserCategories(userId: string | null): Promise<SyncResult> {
    if (!userId) {
      return { success: true, entity: 'user_categories', count: 0 };
    }
    try {
      const { data, error } = await supabase
        .from('user_categories')
        .select('*')
        .eq('user_id', userId)
        .eq('visibile', true);

      if (error) throw error;

      const categories = data || [];
      
      // Pulisci e ricarica le categorie
      await offlineDb.categories.where('user_id').equals(userId).delete();
      
      if (categories.length > 0) {
        const categoriesWithSync = categories.map(cat => ({
          ...cat,
          _syncStatus: 'synced' as const,
          _lastModified: Date.now()
        }));
        
        await offlineDb.categories.bulkPut(categoriesWithSync);
      }

      return {
        success: true,
        entity: 'user_categories',
        count: categories.length
      };
    } catch (error) {
      throw new Error(`Categories sync failed: ${error}`);
    }
  }

  private async syncUserProducts(userId: string | null): Promise<SyncResult> {
    if (!userId) {
      return { success: true, entity: 'user_products', count: 0 };
    }
    try {
      const { data, error } = await supabase
        .from('user_products')
        .select('*')
        .eq('user_id', userId);

      if (error) throw error;

      const products = data || [];
      
      // Rimuovi prodotti esistenti per l'utente e ricarica
      await offlineDb.products.where('user_id').equals(userId).delete();
      
      if (products.length > 0) {
        const productsWithSync = products.map(product => ({
          ...product,
          _syncStatus: 'synced' as const,
          _lastModified: Date.now()
        }));
        
        await offlineDb.products.bulkPut(productsWithSync);
      }

      return {
        success: true,
        entity: 'user_products',
        count: products.length
      };
    } catch (error) {
      throw new Error(`Products sync failed: ${error}`);
    }
  }

  private async syncShoppingLists(userId: string | null): Promise<SyncResult> {
    if (!userId) {
      return { success: true, entity: 'shopping_lists', count: 0 };
    }
    try {
      const { data, error } = await supabase
        .from('shopping_lists')
        .select('*')
        .eq('user_id', userId);

      if (error) throw error;

      const lists = data || [];
      
      // Pulisci e ricarica le liste
      await offlineDb.shoppingLists.where('user_id').equals(userId).delete();
      
      if (lists.length > 0) {
        const listsWithSync = lists.map(list => ({
          ...list,
          _syncStatus: 'synced' as const,
          _lastModified: Date.now()
        }));
        
        await offlineDb.shoppingLists.bulkPut(listsWithSync);
      }

      return {
        success: true,
        entity: 'shopping_lists',
        count: lists.length
      };
    } catch (error) {
      throw new Error(`Shopping lists sync failed: ${error}`);
    }
  }

  private async syncShoppingItems(userId: string | null): Promise<SyncResult> {
    if (!userId) {
      return { success: true, entity: 'shopping_items', count: 0 };
    }
    try {
      // Prima ottieni tutte le liste dell'utente
      const userLists = await offlineDb.shoppingLists
        .where('user_id')
        .equals(userId)
        .toArray();

      if (userLists.length === 0) {
        return {
          success: true,
          entity: 'shopping_items',
          count: 0
        };
      }

      const listIds = userLists.map(list => list.id);
      
      const { data, error } = await supabase
        .from('shopping_items')
        .select('*')
        .in('list_id', listIds);

      if (error) throw error;

      const items = data || [];
      
      // Pulisci tutti gli item e ricarica
      await offlineDb.shoppingItems.clear();
      
      if (items.length > 0) {
        const itemsWithSync = items.map(item => ({
          ...item,
          _syncStatus: 'synced' as const,
          _lastModified: Date.now()
        }));
        
        await offlineDb.shoppingItems.bulkPut(itemsWithSync);
      }

      return {
        success: true,
        entity: 'shopping_items',
        count: items.length
      };
    } catch (error) {
      throw new Error(`Shopping items sync failed: ${error}`);
    }
  }

  private async syncUserShops(userId: string | null): Promise<SyncResult> {
    if (!userId) {
      return { success: true, entity: 'user_shops', count: 0 };
    }
    try {
      const { data, error } = await supabase
        .from('user_shops')
        .select('*')
        .eq('user_id', userId);

      if (error) throw error;

      const shops = data || [];
      
      // Pulisci e ricarica i negozi
      await offlineDb.shops.where('user_id').equals(userId).delete();
      
      if (shops.length > 0) {
        const shopsWithSync = shops.map(shop => ({
          ...shop,
          _syncStatus: 'synced' as const,
          _lastModified: Date.now()
        }));
        
        await offlineDb.shops.bulkPut(shopsWithSync);
      }

      return {
        success: true,
        entity: 'user_shops',
        count: shops.length
      };
    } catch (error) {
      throw new Error(`User shops sync failed: ${error}`);
    }
  }

  private async syncRecurringProducts(userId: string | null): Promise<SyncResult> {
    if (!userId) {
      return { success: true, entity: 'recurring_products', count: 0 };
    }
    try {
      const { data, error } = await supabase
        .from('recurring_products')
        .select('*')
        .eq('user_id', userId)
        .eq('active', true);

      if (error) throw error;

      const recurringProducts = data || [];
      
      // Pulisci e ricarica i prodotti ricorrenti
      await offlineDb.recurringProducts.where('user_id').equals(userId).delete();
      
      if (recurringProducts.length > 0) {
        const productsWithSync = recurringProducts.map(product => ({
          ...product,
          _syncStatus: 'synced' as const,
          _lastModified: Date.now()
        }));
        
        await offlineDb.recurringProducts.bulkPut(productsWithSync);
      }

      return {
        success: true,
        entity: 'recurring_products',
        count: recurringProducts.length
      };
    } catch (error) {
      throw new Error(`Recurring products sync failed: ${error}`);
    }
  }

  private async ensurePrimaryListExists(userId: string | null): Promise<void> {
    try {
      if (!userId) return;
      await primaryListOfflineService.ensurePrimaryListExists(userId);
      OfflineLogger.info('InitialSyncService', 'Primary list ensured');
    } catch (error) {
      OfflineLogger.error('InitialSyncService.ensurePrimaryListExists', error);
    }
  }

  private async storeSyncStatus(status: InitialSyncStatus): Promise<void> {
    try {
      const key = `initial_sync_${status.userId ?? 'unknown'}`;
      localStorage.setItem(key, JSON.stringify(status));
    } catch (error) {
      OfflineLogger.error('InitialSyncService.storeSyncStatus', error);
    }
  }

  async getLastSyncStatus(userId: string | null): Promise<InitialSyncStatus | null> {
    try {
      const key = `initial_sync_${userId ?? 'unknown'}`;
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      OfflineLogger.error('InitialSyncService.getLastSyncStatus', error);
      return null;
    }
  }

  private createFailedStatus(userId: string | null, error: string): InitialSyncStatus {
    return {
      isComplete: false,
      results: [],
      timestamp: Date.now(),
      userId,
      totalEntities: 0,
      successfulEntities: 0
    };
  }

  async validateOfflineDataIntegrity(userId: string | null): Promise<{
    isValid: boolean;
    missingEntities: string[];
    details: Record<string, number>;
  }> {
    if (!userId) {
      OfflineLogger.warn('InitialSyncService.validateOfflineDataIntegrity', 'No user id - skipping validation');
      return {
        isValid: false,
        missingEntities: ['no_user'],
        details: {}
      };
    }

    // ✅ NUOVO: Delega al servizio di validazione
    OfflineLogger.info('InitialSyncService.validateOfflineDataIntegrity', 'Invoking performComprehensiveValidation', {
      userId,
      stack: new Error().stack
    });
    const validation = await syncValidationService.performComprehensiveValidation(userId);
    
    return {
      isValid: validation.isValid,
      missingEntities: validation.missingEntities,
      details: validation.details
    };
  }

  isSyncInProgress(): boolean {
    return this.syncLock;
  }
}

export const initialSyncService = new InitialSyncService();
